/**
 * Assistant search adapter — localStorage-only.
 *
 * The assistant backend (services/assistant/) stores session history in
 * Redis with a 2-hour sliding TTL and no persistent message table. There
 * is no server-side search endpoint to call. The frontend's
 * useSessionList hook (frontend-shell/src/modules/assistant/hooks/
 * useSessionList.ts) caches session metadata (id + first-user-message
 * title + updatedAt) in localStorage under "weco.assistant.sessions".
 *
 * This adapter reads that storage and substring-matches the title.
 *
 * Known limitations:
 *   - Only the FIRST user message of each session is matched — that's
 *     the only text the localStorage entry stores.
 *   - Only sessions the CURRENT browser has touched are searchable;
 *     conversations started on another device are invisible until
 *     backend persistence lands.
 *   - No message-level deep-link (`?focus=`) until backend persistence
 *     lands; the adapter opens the whole conversation.
 *
 * When the assistant module gains a persistent message store, replace
 * this adapter's body with a backend search call and add focus= deep
 * linking. See specs/2026-05-19-global-search-redesign-design.md §5.
 */

import type { GlobalSearchResult, ModuleSearchAdapter } from "../types";

const STORAGE_KEY = "weco.assistant.sessions";
const MAX_RESULTS = 3;

interface SessionEntry {
  id: string;
  title: string;
  updatedAt: number;
}

function readSessions(): SessionEntry[] {
  if (typeof window === "undefined" || !window.localStorage) return [];
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const assistantAdapter: ModuleSearchAdapter = {
  moduleId: "assistant",
  label: "Assistant",
  async search(q, _signal) {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];

    const sessions = readSessions();
    const matches = sessions
      .filter((s) => (s.title || "").toLowerCase().includes(needle))
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .slice(0, MAX_RESULTS);

    return matches.map<GlobalSearchResult>((s) => ({
      id: `assistant:${s.id}`,
      moduleId: "assistant",
      title: s.title,
      subtitle: undefined,
      timestamp: new Date(s.updatedAt).toISOString(),
      href: `/c/${encodeURIComponent(s.id)}`,
    }));
  },
};
